import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveConversation } from '../../contexts/ActiveConversationContext';
import { useConversations } from '../../contexts/ConversationsContext';
import { useMobileDrawer } from '../../contexts/MobileDrawerContext';
import { useTheme } from '../../contexts/ThemeContext';
import { EditableTitle } from '../conversations/EditableTitle';
import {
  IconChevronLeft,
  IconChevronRight,
  IconHamburger,
  IconPencil,
} from '../primitives/Icons';

const THEME_ICONS: Record<string, string> = {
  morning: '🌅',
  afternoon: '☀️',
  evening: '🌙',
};

const THEME_LABEL_KEYS: Record<string, string> = {
  morning: 'themeBadgeMorning',
  afternoon: 'themeBadgeAfternoon',
  evening: 'themeBadgeEvening',
};

export function ChatHeader() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { conversationId } = useActiveConversation();
  const { conversations, rename } = useConversations();
  const { open, desktopCollapsed, toggleDesktopCollapsed, isMobile } = useMobileDrawer();

  const [editing, setEditing] = useState(false);

  // Exit edit mode if the active conversation changes mid-edit (e.g. user
  // navigates to a different conversation via the sidebar).
  useEffect(() => {
    setEditing(false);
  }, [conversationId]);

  const conv = conversations.find((c) => c.id === conversationId);
  const title = conv?.title ?? '';

  return (
    <div className="chat-header">
      <div className="chat-header-left">
        {/* Mobile: hamburger opens the drawer. */}
        <button
          type="button"
          className="collapse-btn mobile-only"
          onClick={open}
          title={t('newChat')}
          aria-label={t('newChat')}
        >
          <IconHamburger />
        </button>
        {/* Desktop: chevron toggles collapsed sidebar. */}
        <button
          type="button"
          className="collapse-btn collapse-btn-desktop"
          onClick={toggleDesktopCollapsed}
          title={desktopCollapsed ? t('expandSidebar') : t('collapseSidebar')}
          aria-label={desktopCollapsed ? t('expandSidebar') : t('collapseSidebar')}
        >
          {desktopCollapsed ? <IconChevronRight /> : <IconChevronLeft />}
        </button>

        {conversationId ? (
          <>
            <EditableTitle
              value={title}
              placeholder={t('untitled')}
              onCommit={(v) => void rename(conversationId, v)}
              editing={editing}
              setEditing={setEditing}
              className={`chat-header-title editable${isMobile ? ' editable-mobile' : ' editable-desktop'}`}
              inputClassName="chat-header-title-input"
              singleClick={isMobile}
              inputAriaLabel={t('renameConversation')}
            />
            {!isMobile && !editing && (
              <button
                type="button"
                className="title-edit-btn"
                onClick={() => setEditing(true)}
                aria-label={t('renameConversation')}
                title={t('rename')}
              >
                <IconPencil />
              </button>
            )}
          </>
        ) : (
          <span className="chat-header-title">{t('newChat')}</span>
        )}
      </div>
      <div className="chat-header-right">
        <div className="theme-badge">
          <span className="theme-dot" />
          <span>
            {THEME_ICONS[theme]} {t(THEME_LABEL_KEYS[theme] ?? '')}
          </span>
        </div>
      </div>
    </div>
  );
}
